import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { ReCaptchaCheckbox } from '@/Domain/components/Auth/ReCaptchaCheckbox';
import { GoogleButton } from '@/Domain/components/Auth/SocialButton/GoogleButton';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { SocialPlatformEnum } from '@/Domain/enum/SocialPlatform.enum';
import { useRegister } from '@/Domain/hooks/Auth/useRegister.hook';
import { useSocialRegister } from '@/Domain/hooks/Auth/useSocialRegister.hook';

export const RegisterPage = () => {
  const { t } = useTranslation('register');
  const { track } = useAnalytics();

  const { control, useV2, register, handleSubmit, onSubmit, errors, onVerify, isPending } =
    useRegister();
  const { handleSocialRegister } = useSocialRegister();

  const handleTrack = () =>
    track('register_back_click', {
      event_category: 'User Registration',
      event_label: 'register_form',
    });

  return (
    <>
      <div className="font-semibold mb-8 text-xl text-center text-black">{t('title')}</div>
      <div className="grid gap-4 mb-4">
        <GoogleButton onClick={(event) => handleSocialRegister(event, SocialPlatformEnum.Google)} />
      </div>
      <div className="relative flex py-1 items-center">
        <div className="flex-grow border-t border-gray-400"></div>
        <span className="flex-shrink mx-4 text-gray-400 text-xs">or</span>
        <div className="flex-grow border-t border-gray-400"></div>
      </div>

      <form
        id="register-email-form"
        className="grid space-y-4 mb-3"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div>
          <label htmlFor="email" className="text-sm text-gray-950">
            {t('form.email')}
          </label>
          <Input
            id="email"
            {...register('username')}
            placeholder={t('form.email_placeholder')}
            error={!!errors.username}
          />
          {errors.username && (
            <span className="text-sm text-red-700">{errors.username.message}</span>
          )}
        </div>

        <div>
          <label htmlFor="password1" className="text-sm text-gray-950">
            {t('form.password')}
          </label>
          <Input
            id="password1"
            type="password"
            placeholder={t('form.password_placeholder')}
            {...register('password1')}
            error={!!errors.password1}
          />
          {errors.password1 && (
            <span className="text-sm text-red-700">{errors.password1.message}</span>
          )}
        </div>

        <div>
          <label htmlFor="password2" className="text-sm text-gray-950">
            {t('form.confirm_password')}
          </label>
          <Input
            id="password2"
            placeholder={t('form.password_placeholder')}
            type="password"
            {...register('password2')}
            error={!!errors.password2}
          />
          {errors.password2 && (
            <span className="text-sm text-red-700">{errors.password2.message}</span>
          )}
        </div>

        <div>
          <GoogleReCaptcha onVerify={onVerify} />
        </div>
        <Button variant="dark" type="submit" disabled={isPending}>
          {isPending ? <Spinner /> : t('button.submit')}
        </Button>
        {useV2 && (
          <Controller
            name="recaptcha_response_v2"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange }, fieldState: { invalid } }) => (
              <ReCaptchaCheckbox onChange={onChange} isInvalid={invalid} />
            )}
          />
        )}
      </form>

      <p className="text-sm">
        <Trans
          i18nKey="register:terms_of_use"
          components={{
            terms_link: (
              <Link
                className="text-gray-950 font-medium"
                to="https://www.quickchat.ai/terms"
                target="_blank"
              />
            ),
            privacy_link: (
              <Link
                className="text-gray-950 font-medium"
                to="https://www.quickchat.ai/privacy"
                target="_blank"
              />
            ),
          }}
        />
      </p>

      <p className="!mt-8 text-center text-sm">
        {t('have_account')}{' '}
        <Link className="text-gray-950 font-medium" to="/" onClick={handleTrack}>
          {t('link.login')}
        </Link>
      </p>
    </>
  );
};

export default RegisterPage;
